import { useState } from "react";

import ChangelogCard from "./components/ChangelogCard";
import StatsPanel from "./components/StatsPanel";
import LoadingSpinner from "./components/LoadingSpinner";
import EmptyState from "./components/EmptyState";
import { Pagination } from "./components/ui/Pagination";
import { Input } from "./components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/Select";

import { useChangelog, useConfig } from "./hooks/useApi";
import { useSeoMeta } from "./hooks/useSeoMeta";
import { debounce } from "./utils";

export function App() {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const { data: changelogResponse, isLoading } = useChangelog(page, limit, searchTerm);
  const { data: config } = useConfig();

  const repoLabel = config
    ? config.repository.displayName ||
      `${config.repository.owner}/${config.repository.name}`
    : null;
  const brandName = config?.branding?.name || "Changelog";

  useSeoMeta({
    title: repoLabel
      ? `${brandName} · ${repoLabel} · Histórico público de versões`
      : `${brandName} · Histórico de versões`,
    description: repoLabel
      ? `Histórico público de versões de ${repoLabel} com notas de release organizadas por tag. Acompanhe novidades, melhorias, correções e atualizações de segurança em cada atualização.`
      : "Notas de release organizadas por tag: novidades, melhorias, correções e atualizações de segurança publicadas em cada versão do produto.",
    canonicalPath: "/",
    ogType: "website",
    ogImage: "/og/home.png",
    jsonLd: repoLabel
      ? {
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: `${brandName} · ${repoLabel}`,
          url: `${window.location.origin}/`,
          description: `Histórico de versões de ${repoLabel}.`,
          inLanguage: "pt-BR",
          publisher: {
            "@type": "Organization",
            name: brandName,
          },
        }
      : null,
  });

  const debouncedSearch = debounce((term: string) => {
    setSearchTerm(term);
    setPage(1);
  }, 250);

  const changelog = changelogResponse?.data || [];

  return (
    <>
      <header className="mb-12">
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-medium mb-3">
          Histórico de versões
        </p>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold text-foreground leading-[1.05] tracking-tight">
          O que mudou na{" "}
          {repoLabel ? (
            <span className="font-mono text-[0.75em] tracking-tight text-primary">
              {repoLabel}
            </span>
          ) : (
            "cada versão"
          )}
        </h1>
        <p className="mt-5 text-base text-muted-foreground leading-relaxed">
          Resumo de cada versão, com novidades, correções e o
          contexto que importa para quem usa o produto.
        </p>
      </header>

      <StatsPanel />

      <div className="mb-10 flex flex-col sm:flex-row gap-3">
        <label htmlFor="changelog-search" className="sr-only">
          Buscar
        </label>
        <Input
          id="changelog-search"
          type="search"
          placeholder="Buscar por tag ou conteúdo"
          onChange={(e) => debouncedSearch(e.target.value)}
          className="flex-1"
        />
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs uppercase tracking-wider text-muted-foreground hidden sm:inline">
            Por página
          </span>
          <Select
            value={String(limit)}
            onValueChange={(v) => {
              setLimit(Number(v));
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[6rem] h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading && (
        <div className="py-8">
          <LoadingSpinner />
        </div>
      )}

      {!isLoading && changelog.length === 0 && (
        <EmptyState
          title="Nenhuma release encontrada"
          description={
            searchTerm
              ? "Tente ajustar sua busca."
              : "Ainda não há releases publicadas."
          }
          actionText={searchTerm ? "Limpar busca" : undefined}
          onAction={
            searchTerm
              ? () => {
                  setSearchTerm("");
                  setPage(1);
                }
              : undefined
          }
        />
      )}

      {!isLoading && changelog.length > 0 && (
        <>
          <ol className="space-y-10 list-none p-0 m-0">
            {changelog.map((release, idx) => (
              <li key={release.id}>
                <ChangelogCard release={release} index={idx} currentPage={page} />
              </li>
            ))}
          </ol>

          {changelogResponse?.meta && changelogResponse.meta.totalPages > 1 && (
            <div className="mt-12 pt-6 border-t border-border">
              <Pagination
                currentPage={page}
                totalPages={changelogResponse.meta.totalPages}
                itemsPerPage={limit}
                onPageChange={setPage}
                onItemsPerPageChange={setLimit}
              />
            </div>
          )}
        </>
      )}
    </>
  );
}

export default App;
